import { DEFAULT_FORM_VALUES } from '@/lib/env';

export interface CalculatorInputs {
  // Workflow type selection (wizard mode)
  useCredentialRequests: boolean;
  useAccessRequests: boolean;
  useVisitorRequests: boolean;
  
  // Staffing inputs
  cardholderAdmins: number;
  costPerEmployee: number;
  
  // Visitor inputs
  hasVisitorRegistration: boolean;
  visitorsPerMonth: number;
  timePerVisitor: number; // in minutes - manual time
  
  // Process inputs - manual times
  timePerRequest: number; // in minutes - for credentials and access (manual)
  
  // AccessNow efficiency times
  accessNowTimePerRequest: number; // in minutes - for credentials and access
  accessNowTimePerVisitor: number; // in minutes - for visitors
  
  // Database inputs
  cardholderDatabaseSize: number;
  cardReplacementPercentage: number;
  staffTurnoverRate: number;
  staffGrowthRate: number;
  accessRequestsPerCardholder: number;
  
  // Capacity planning
  extraRequestCapacityPercentage: number;
  
  // Simple calculator specific inputs
  credentialRequestsPerMonth: number;
  accessRequestsPerMonth: number;
  
  // Workflow differentiation
  differentiateEmployeeContractor: boolean;
  
  // Risk Mitigation Value (operational insurance)
  operationalInsuranceValue: number;
  
  // Industry and country selection
  industry?: string; // Industry key for advanced calculator
  country?: string; // Country code detected from headers
}

export interface PricingConfig {
  workflowPrice: number; // Workflow with 1000 requests
  extraRequestsSmallPrice: number; // Extra 1000 Requests
  extraRequestsLargePrice: number; // Extra 30,000 Requests
}

export interface AccessNowPricing {
  workflows: number; // Number of workflows needed
  extraRequestsSmall: number; // 1000-request packages
  extraRequestsLarge: number; // 30,000-request packages  
  totalCost: number;
  workflowCounts: {
    credential: number;
    access: number;
    visitor: number;
  };
  requestsBreakdown: {
    includedRequests: number; // From workflows (1000 per workflow)
    additionalRequests: number;
    totalRequests: number;
  };
  costBreakdown: {
    workflowsCost: number;
    extraRequestsCost: number;
  };
}

export interface CapacityValidation {
  allocatedHoursPerYear: number;
  requiredHoursPerYear: number;
  capacityRatio: number; // 0-1, where >1 means over capacity
  isOverCapacity: boolean;
  additionalStaffNeeded?: number;
  // AccessNow capacity calculations
  accessNowRequiredHoursPerYear: number;
  accessNowCapacityRatio: number;
  accessNowIsOverCapacity: boolean;
  accessNowAdditionalStaffNeeded?: number;
}

export interface CalculationResults {
  totalSavings: number;
  totalManualCost: number;
  totalManualHours: number; // Total manual hours required (from base requests)
  totalAccessNowHours: number; // Total AccessNow hours required (from base requests)
  businessEfficiencyValue: number;
  fteReduction: number;
  totalHoursSaved: number;
  credentialWorkflowSavings: number;
  accessWorkflowSavings: number;
  visitorWorkflowSavings: number;
  totalRequestsPerYear: number; // Licensing requests (with capacity buffer)
  baseRequestsPerYear: number; // Base operational requests (without capacity buffer)
  workflowSummary: WorkflowSummary[];
  accessNowPricing: AccessNowPricing;
  capacityValidation: CapacityValidation; // Capacity validation info
}

export interface WorkflowSummary {
  name: string;
  requestsPerYear: number;
  hoursPerYear: number;
  hoursSavedPerYear: number;
  savingsPerYear: number;
  manualLaborCost: number;
}

export const DEFAULT_VALUES: CalculatorInputs = DEFAULT_FORM_VALUES;